// [S-061c] Pre-negotiation brief hooks. Backed by the admin REST API:
//
//   POST /briefs       → compose + persist
//   GET  /briefs       → list (newest first, caller's tenant only)
//   GET  /briefs/:id   → fetch a single brief
//
// Briefs are cached under [briefs, id] / [briefs, list]. The mutation
// invalidates the list so a freshly-composed brief shows up on
// /briefs without a hard refresh.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type BriefL1Section = {
  layerSource: "L1";
  templateId: string;
  citationLabel: string;
  bullets: string[];
};

export type BriefL2Section = {
  layerSource: "L2";
  dealCount: number;
  dateRange: string;
  citationLabel: string;
  bullets: string[];
};

export type BriefInput = {
  dealType:
    | "feature_film"
    | "tv_series"
    | "streaming"
    | "documentary"
    | "commercial"
    | "short_form";
  guild: "SAG" | "DGA" | "WGA" | "IATSE" | "AFM" | "PGA";
  talentTier: "basic" | "professional" | "enterprise";
  opposingCounselPosture: string;
  keyTermsInFlight: string;
};

export type Brief = {
  id: string;
  tenantId: string | null;
  input: BriefInput;
  l1Section: BriefL1Section | null;
  l2Section: BriefL2Section | null;
  composedBy: string;
  composedAt: string;
};

export const BRIEFS_KEY = ["briefs"] as const;

export function useComposeBrief(): UseMutationResult<Brief, Error, BriefInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<Brief>("/briefs", { method: "POST", body: input }),
    onSuccess: (brief) => {
      qc.setQueryData([...BRIEFS_KEY, brief.id], brief);
      void qc.invalidateQueries({ queryKey: [...BRIEFS_KEY, "list"] });
    },
  });
}

export function useBriefs(): UseQueryResult<Brief[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...BRIEFS_KEY, "list"] as const,
    queryFn: async () => {
      const res = await api<{ briefs: Brief[] }>("/briefs");
      return res.briefs;
    },
  });
}

export function useBrief(briefId: string | undefined): UseQueryResult<Brief, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...BRIEFS_KEY, briefId] as const,
    enabled: briefId !== undefined && briefId.length > 0,
    queryFn: () =>
      api<Brief>(`/briefs/${encodeURIComponent(briefId!)}`),
  });
}
